import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useNotify, usePermissions, useRecordContext, useRefresh, useTranslate } from 'react-admin';
import { refundPayment, schedulePendingRefund } from '../../services/paymentsService';
import { getSaleById } from '../../services/salesService';
import { canRefundPaymentRecord, canRefundPayments } from '../../utils/paymentUtils';
import { getSaleProducts } from '../../utils/salesUtils';
import RefundNotificationFields from '../refunds/RefundNotificationFields';

const SAME_DAY_NOTE =
  'Aynı gün iade yapılamadı. Bir sonraki gün (işlem günü kapandıktan sonra) iade yapılacak.';

const buildReasonPayload = ({ reasonMail, reasonSms, reasonPush }) => ({
  refundReasonMail: reasonMail.trim() || undefined,
  refundReasonSms: reasonSms.trim() || undefined,
  refundReasonPush: reasonPush.trim() || undefined,
});

const PaymentRefundActions = () => {
  const translate = useTranslate();
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSale, setLoadingSale] = useState(false);
  const [mode, setMode] = useState('amount'); // 'amount' | 'product'
  const [amount, setAmount] = useState('');
  const [reasonMail, setReasonMail] = useState('');
  const [reasonSms, setReasonSms] = useState('');
  const [reasonPush, setReasonPush] = useState('');
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState({}); // { 'groupKey::productId': count }
  const [pendingRefund, setPendingRefund] = useState(null);

  if (!record?.id) return null;
  if (!canRefundPayments(permissions)) return null;
  if (!canRefundPaymentRecord(record)) return null;

  const total = Number(record.total ?? 0);
  const refunded = Number(record.transaction?.refundAmount ?? 0);
  const maxRefundable = Math.max(0, total - refunded);

  // Garanti aynı gün kısmi/ürün bazlı iadeye izin vermez; yalnızca tam iade (void) yapılabilir.
  const paymentDate = record.createdate ?? record.transaction?.authdate;
  const isSameDay = paymentDate ? moment(paymentDate).isSame(moment(), 'day') : false;

  const keyOf = (p) => `${p.groupKey}::${p.productId}`;

  const openDialog = async () => {
    setAmount(String(maxRefundable));
    const defaultNote = isSameDay ? SAME_DAY_NOTE : (record.transaction?.refundNote ?? '');
    setReasonMail(defaultNote);
    setReasonSms('');
    setReasonPush('');
    setMode('amount');
    setSelected({});
    setProducts([]);
    setPendingRefund(null);
    setOpen(true);

    const saleId = typeof record.sale === 'string' ? record.sale : record.sale?.id;
    if (saleId) {
      setLoadingSale(true);
      try {
        const sale = await getSaleById(saleId);
        const pending = sale?.pendingRefund;
        const pendingActive =
          pending &&
          typeof pending === 'object' &&
          pending.status !== 'done' &&
          pending.status !== 'failed';
        setPendingRefund(pendingActive ? pending : null);

        const saleProducts = getSaleProducts(sale);
        setProducts(saleProducts);

        if (pendingActive && Array.isArray(pending.items) && pending.items.length) {
          const preselected = {};
          for (const item of pending.items) {
            const match = saleProducts.find(
              (p) => p.groupKey === item.groupKey && p.productId === item.productId,
            );
            if (match) {
              preselected[keyOf(match)] = Math.min(Number(item.count ?? match.count), match.count);
            }
          }
          if (Object.keys(preselected).length) {
            setMode('product');
            setSelected(preselected);
          }
        } else if (pendingActive && pending.isFull) {
          setMode('amount');
          setAmount(String(maxRefundable));
        }
      } catch (error) {
        notify('Satış ürünleri yüklenemedi.', { type: 'warning' });
      } finally {
        setLoadingSale(false);
      }
    }
  };

  const toggleProduct = (p) => {
    const k = keyOf(p);
    setSelected((prev) => {
      const next = { ...prev };
      if (next[k] != null) delete next[k];
      else next[k] = p.count;
      return next;
    });
  };

  const setProductCount = (p, value) => {
    const k = keyOf(p);
    const n = Math.max(1, Math.min(p.count, Number(value) || 1));
    setSelected((prev) => ({ ...prev, [k]: n }));
  };

  const selectedItems = products
    .filter((p) => selected[keyOf(p)] != null)
    .map((p) => ({
      groupKey: p.groupKey,
      productId: p.productId,
      count: selected[keyOf(p)],
    }));

  const selectedTotal = products
    .filter((p) => selected[keyOf(p)] != null)
    .reduce((sum, p) => sum + p.price * selected[keyOf(p)], 0);

  // Tüm ürünler tam adetle seçiliyse bu bir "tam iade" (void) sayılır.
  const isFullProductSelection =
    products.length > 0 && products.every((p) => selected[keyOf(p)] === p.count);

  // Aynı gün + kısmi ürün seçimi -> Garanti reddeder.
  const sameDayPartialBlocked = mode === 'product' && isSameDay && !isFullProductSelection;

  const handleRefund = async () => {
    let payload;
    let confirmMsg;

    if (mode === 'product') {
      if (selectedItems.length === 0) {
        notify('En az bir ürün seçin.', { type: 'warning' });
        return;
      }
      if (sameDayPartialBlocked) {
        notify(
          'Aynı gün kısmi/ürün bazlı iade Garanti tarafından reddedilir. Tam iade yapın veya ertesi gün tekrar deneyin.',
          { type: 'warning', autoHideDuration: 8000 },
        );
        return;
      }
      payload = {
        paymentId: record.id,
        items: selectedItems,
        ...buildReasonPayload({ reasonMail, reasonSms, reasonPush }),
      };
      confirmMsg = `${selectedItems.length} ürün satırı (~${selectedTotal} TL) iade edilecek. Emin misiniz?`;
    } else {
      const useAmount = amount === '' ? maxRefundable : Number(amount);
      if (!useAmount || useAmount <= 0 || useAmount > maxRefundable) {
        notify('Geçersiz iade tutarı.', { type: 'warning' });
        return;
      }
      payload = {
        paymentId: record.id,
        amount: useAmount,
        ...buildReasonPayload({ reasonMail, reasonSms, reasonPush }),
      };
      confirmMsg =
        useAmount >= maxRefundable
          ? `${useAmount} TL tutarında tam iade yapılacak. Emin misiniz?`
          : `${useAmount} TL kısmi iade yapılacak. Emin misiniz?`;
    }

    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true);
    try {
      await refundPayment(payload);
      notify(
        pendingRefund
          ? 'İade tamamlandı. Planlı iade süreci kapatıldı (İade süreci başarılı).'
          : 'İade başarıyla tamamlandı.',
        { type: 'success' },
      );
      setOpen(false);
      refresh();
    } catch (error) {
      notify(error?.message || 'İade işlemi başarısız.', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSchedule = async () => {
    // Ürün modunda seçilenleri, değilse tüm satışı planla.
    const items = mode === 'product' ? selectedItems : undefined;
    if (mode === 'product' && (!items || items.length === 0)) {
      notify('Planlamak için en az bir ürün seçin.', { type: 'warning' });
      return;
    }

    const confirmMsg =
      items && items.length > 0
        ? `${items.length} ürün satırı ertesi gün otomatik iade edilmek üzere planlanacak. Onaylıyor musunuz?`
        : 'Bu satışın tamamı ertesi gün otomatik iade edilmek üzere planlanacak. Onaylıyor musunuz?';
    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true);
    try {
      await schedulePendingRefund({
        paymentId: record.id,
        items,
        reason: reasonMail.trim() || SAME_DAY_NOTE,
      });
      notify('İade ertesi güne planlandı. Sistem otomatik gerçekleştirecek.', {
        type: 'success',
      });
      setOpen(false);
      refresh();
    } catch (error) {
      notify(error?.message || 'İade planlanamadı.', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('payments.refund_action_desc', {
          _: 'Garanti TEST/PROD üzerinden kart ödemesini iade eder. İade edilebilir tutar:',
        })}{' '}
        <strong>{maxRefundable} TL</strong>
      </Typography>
      <Button variant="contained" color="warning" onClick={openDialog}>
        {translate('payments.make_refund', { _: 'İade yap' })}
      </Button>

      <Dialog open={open} onClose={() => !submitting && setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{translate('payments.dialog_title', { _: 'Ödeme iadesi' })}</DialogTitle>
        <DialogContent>
          {pendingRefund && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Bu satışta{' '}
              <strong>
                {pendingRefund.scheduledFor
                  ? moment(pendingRefund.scheduledFor).format('LL LT')
                  : 'ertesi gün'}
              </strong>{' '}
              için planlı iade var. Cron&apos;dan önce aynı ürünleri (veya tam tutarı) şimdi iade
              ederseniz planlı süreç kapanır ve satış listesinde{' '}
              <strong>İade süreci başarılı</strong> görünür; Garanti&apos;ye tekrar istek gitmez.
            </Alert>
          )}

          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)} sx={{ mb: 1 }}>
            <FormControlLabel
              value="amount"
              control={<Radio />}
              label={translate('payments.amount_based', { _: 'Tutar bazlı' })}
            />
            <FormControlLabel
              value="product"
              control={<Radio />}
              label={translate('payments.product_based', { _: 'Ürün bazlı' })}
              disabled={products.length === 0}
            />
          </RadioGroup>

          {mode === 'amount' && (
            <TextField
              label={translate('payments.refund_amount_label', { _: 'İade tutarı (TL)' })}
              type="number"
              fullWidth
              margin="normal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0.01, max: maxRefundable, step: 0.01 }}
              helperText={`${translate('payments.max_label', { _: 'Maksimum:' })} ${maxRefundable} TL`}
            />
          )}

          {mode === 'product' && isSameDay && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {translate('payments.same_day_garanti_warning', {
                _: 'Bu ödeme bugün yapıldığı için Garanti yalnızca tam iade kabul eder. Sadece bir ürünü iade etmek için işlemin ertesi günü tekrar deneyin.',
              })}
            </Alert>
          )}

          {mode === 'product' && (
            <Box sx={{ my: 1 }}>
              {loadingSale ? (
                <Typography variant="body2" color="text.secondary">
                  {translate('payments.loading_products', { _: 'Ürünler yükleniyor...' })}
                </Typography>
              ) : products.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {translate('payments.no_refundable_products', {
                    _: 'Bu satışta iade edilebilir ürün bulunamadı.',
                  })}
                </Typography>
              ) : (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>{translate('payments.col_product', { _: 'Ürün' })}</TableCell>
                        <TableCell align="right">
                          {translate('payments.col_unit', { _: 'Birim' })}
                        </TableCell>
                        <TableCell align="right">
                          {translate('payments.col_count', { _: 'Adet' })}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((p) => {
                        const k = keyOf(p);
                        const checked = selected[k] != null;
                        return (
                          <TableRow key={k} hover>
                            <TableCell padding="checkbox">
                              <Checkbox checked={checked} onChange={() => toggleProduct(p)} />
                            </TableCell>
                            <TableCell>
                              {p.title}
                              <Typography variant="caption" color="text.secondary" display="block">
                                {p.type}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{p.price} TL</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                disabled={!checked}
                                value={checked ? selected[k] : p.count}
                                onChange={(e) => setProductCount(p, e.target.value)}
                                inputProps={{ min: 1, max: p.count, style: { width: 50 } }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {' '}
                                / {p.count}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    {translate('payments.selected_total', { _: 'Seçilen tahmini tutar:' })}{' '}
                    <strong>{selectedTotal} TL</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {translate('payments.same_day_note_footer', {
                      _: 'Not: Garanti aynı gün kısmi iadeye izin vermez; kısmi/ürün bazlı iade ertesi gün yapılabilir.',
                    })}
                  </Typography>
                </>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <RefundNotificationFields
            reasonMail={reasonMail}
            reasonSms={reasonSms}
            reasonPush={reasonPush}
            onMailChange={setReasonMail}
            onSmsChange={setReasonSms}
            onPushChange={setReasonPush}
            eventName={record?.event?.name || record?.eventName}
            previewAmount={mode === 'amount' ? amount : selectedTotal}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            {translate('payments.cancel', { _: 'Vazgeç' })}
          </Button>
          <Button onClick={handleSchedule} disabled={submitting} color="primary">
            {translate('payments.schedule_next_day', { _: 'Ertesi güne planla (otomatik iade)' })}
          </Button>
          <Button
            onClick={handleRefund}
            color="warning"
            variant="contained"
            disabled={submitting || sameDayPartialBlocked}
          >
            {submitting
              ? translate('payments.processing', { _: 'İşleniyor...' })
              : translate('payments.confirm_refund', { _: 'İadeyi onayla' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentRefundActions;
